import Cookies from 'js-cookie';
import UAParser from 'ua-parser-js';
import { v4 } from 'uuid';

import { getUrlParams, parseJwt } from '@/helpers';
import IApiResponse from '@/interfaces/response';
import { setUser } from '@/redux/auth/slice';
import { store } from '@/redux/store';
import { ACCOUNTS_AMBIENT, API_BASE_URL, API_V4_BASE_URL, BUILD_NUMBER, COOKIE_DOMAIN } from '@/settings';

import { post } from './api';

let loginPromise: Promise<any> | null = null;

export enum enTokenKey {
  token = 'newAuthToken',
  expiration = 'tokenExp',
  refreshToken = 'refreshToken'
}

type TokenObject = Record<keyof typeof enTokenKey, string>;

export const tenantLogin = (producerId: number) => {
  const { refreshToken } = getTokens();
  return post({
    withCredentials: false,
    url: '/oauth/change-user-by-tenantid/' + producerId,
    baseURL: API_BASE_URL,
    headers: {
      RefreshToken: refreshToken
    }
  }).then(response => {
    const token = response.data?.data?.replace('Bearer ', '') || 'invalid';
    if (token === 'invalid') return false;
    updateToken(token);
    return true;
  });
};

export const getTokens = (): TokenObject => {
  return {
    refreshToken: Cookies.get(enTokenKey.refreshToken) ?? localStorage.getItem(enTokenKey.refreshToken) ?? null,
    token: Cookies.get(enTokenKey.token) ?? localStorage.getItem(enTokenKey.token) ?? null,
    expiration: Cookies.get(enTokenKey.expiration) ?? localStorage.getItem(enTokenKey.expiration) ?? null
  };
};

const setTokens = (args: Partial<TokenObject>) => {
  const keys = Object.keys(args) as Array<keyof TokenObject>;
  keys.forEach(key => {
    Cookies.set(enTokenKey[key], args[key], { domain: COOKIE_DOMAIN });
    localStorage.setItem(enTokenKey[key], args[key]);
  });
};

export const login = (accountsToken: string, courseHash = '') => {
  if (loginPromise) return loginPromise;

  loginPromise = new Promise(async (resolve, reject) => {
    try {
      const response = await post<IApiResponse>({
        url: '/oauth/eduzzaccount/validate',
        withCredentials: true,
        baseURL: API_V4_BASE_URL,
        data: { token: accountsToken, ...getDeviceInfo(), course_hash: courseHash }
      });

      if (!response?.data?.data) {
        logout();
        reject('token undefined');
      }

      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { token, refresh_token } = response?.data?.data;

      updateToken(token, refresh_token);
      resolve(parseJwt(token));
    } catch (err) {
      logout();
      reject(err);
    } finally {
      loginPromise = null;
    }
  });

  return loginPromise;
};

export const makeLogin = async (accountsToken: string, courseHash = '') => {
  return login(accountsToken, courseHash);
};

export const isPossibleRefresh = async () => {
  try {
    const { refreshToken, token } = getTokens();

    if (!refreshToken || !token) return false;

    if (!isExpiredToken()) return true;

    const { data } = await post({
      url: '/oauth/refresh',
      withCredentials: true,
      baseURL: API_V4_BASE_URL,
      headers: {
        RefreshToken: refreshToken,
        Authorization: `Bearer ${token}`,
        FrontVersion: BUILD_NUMBER || 'local'
      },
      params: {
        startWhen: 'isPossibleRefresh'
      }
    });

    const newToken = data?.data?.token.replace('Bearer ', '') || 'invalid';

    if (newToken === 'invalid') return false;

    updateToken(newToken);

    return true;
  } catch (err) {
    logout();

    return false;
  }
};

export const clearTokens = () => {
  const keys = Object.keys(enTokenKey) as Array<keyof typeof enTokenKey>;
  keys.forEach(key => {
    Cookies.remove(enTokenKey[key], { domain: COOKIE_DOMAIN });
    localStorage.removeItem(enTokenKey[key]);
  });
};

export const isExpiredToken = () => {
  const exp = getExpiration();
  const currentTime = new Date().getTime() / 1000;

  return exp && exp < currentTime;
};

export const getUserInfo = () => {
  const { token } = getTokens();

  if (token) {
    return parseJwt(token);
  }

  return null;
};

const getExpiration = () => {
  const { expiration } = getTokens();

  if (parseInt(expiration) > 0) {
    return parseInt(expiration) / 1000;
  }

  return 0;
};

export const updateToken = (token: string, refreshToken = '') => {
  if (!token) {
    return;
  }

  if (token === 'invalid') {
    clearTokens();
    logout();
  }

  const claims = parseJwt(token);
  const tokenTtl = claims.exp - claims.iat;
  const tokenExpiration = new Date();

  setTokens({
    token,
    expiration: String(tokenExpiration.getSeconds() + tokenTtl - 120)
  });

  if (refreshToken) {
    setTokens({
      refreshToken
    });
  }

  store.dispatch(setUser(parseJwt(token)));
};

export const logout = (redirect = '') => {
  clearTokens();

  if (redirect.length <= 0) {
    redirect = window.location.pathname + window.location.search;
  }

  (window as any)?.Eduzz?.Accounts?.logout({
    redirectTo: window.location.origin + redirect,
    env: ACCOUNTS_AMBIENT
  });
};

export const getDeviceInfo = (): { device_id: string; device_name: string } => {
  const params = getUrlParams();
  let deviceId = params.device_id || localStorage.getItem('deviceId');
  let deviceName = params.device_name || localStorage.getItem('deviceName');

  if (!deviceId) {
    deviceId = setDeviceId();
  }

  if (!deviceName) {
    deviceName = setDeviceName();
  }

  return {
    device_id: deviceId,
    device_name: deviceName
  };
};

const setDeviceId = () => {
  const deviceId = v4();

  localStorage.setItem('deviceId', deviceId.toString());

  return deviceId;
};

const setDeviceName = () => {
  const parser = new UAParser();
  const {
    browser: { name: browser },
    os: { name: device }
  } = parser.getResult();
  const deviceName = `${browser} - ${device}`;

  localStorage.setItem('deviceName', deviceName);

  return deviceName;
};
